// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ErrorState
 * @package @geenius/analytics/react-css
 * @description Internal recovery surface shared by React CSS analytics data
 * components when a fetch or rendering boundary fails.
 */

import { Button } from "@geenius/ui/react-css";
import type React from "react";
import { cx } from "../lib/cx";

/** Error value accepted by React CSS data-rendering components. */
export type AnalyticsErrorStateValue = Error | string | null | undefined;

/** Returns a user-visible message for a component error state. */
export function getAnalyticsErrorMessage(
  error: AnalyticsErrorStateValue,
): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "Analytics data could not be loaded.";
}

interface ErrorStateProps {
  error: AnalyticsErrorStateValue;
  title?: string;
  onRetry?: () => void;
  className?: string;
}

/** Renders a compact, accessible error state with optional retry recovery. */
export function ErrorState({
  className,
  error,
  onRetry,
  title = "Unable to load analytics",
}: ErrorStateProps): React.ReactElement {
  return (
    <div
      className={cx("gn-analytics-error-state", className)}
      data-analytics-error-state
      role="alert"
      aria-live="assertive"
    >
      <strong className="gn-analytics-error-state__title">{title}</strong>
      <p className="gn-analytics-error-state__message">
        {getAnalyticsErrorMessage(error)}
      </p>
      {onRetry ? (
        <Button
          type="button"
          variant="secondary"
          className="gn-analytics-error-state__retry"
          onClick={onRetry}
        >
          Retry
        </Button>
      ) : null}
    </div>
  );
}
